/**
 * `/impersonationen` (P1, docs/KONZEPT-ADMIN-BEREICH.md § "Informationsarchitektur & UI":
 * "Aktive Grants plattformweit + Historie"). Ausschließlich über `GET /api/admin/impersonations`
 * (Migration 374, siehe `apps/api/src/routes/admin.ts` `impersonationsQuerySchema`/
 * `ImpersonationListItem` — Filter-/Cursor-/Antwortformat von dort 1:1 übernommen).
 *
 * Kein Drawer hier (anders als `/audit`): der Auftrag verlangt für diese Seite nur Tabelle +
 * "nur aktive"-Filter + Paginierung, kein Payload-Feld existiert in `ImpersonationListItem` — jede
 * Spalte ist bereits eine flache, fuer sich lesbare Angabe.
 *
 * Paginierung identisch zum Muster in `AuditLogSeite.tsx` (`nextCursor` ueber `zusatz`, lokale
 * Akkumulation der Seiten, "Mehr laden"-Knopf statt Infinite-Scroll).
 */
import { useEffect, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { formatDateTime } from "../../ui/format";
import type { StatusMeta } from "../../ui/format";
import { EmptyState, ErrorNote, LoadingRows, PageHeader, StatusPill, Checkbox } from "../../ui/components";

export interface ImpersonationListItem {
  id: string;
  adminId: string;
  /** NULL, wenn der Mandant geloescht wurde - der Grant bleibt als Nachweis stehen (sql/706/707). */
  tenantId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  reason: string;
  createdAt: string;
  expiresAt: string;
  endedAt: string | null;
  aktiv: boolean;
}

const AKTIV_META: StatusMeta = { label: "Aktiv", tone: "success" };
const BEENDET_META: StatusMeta = { label: "Beendet", tone: "neutral" };

export function ImpersonationenSeite() {
  const [nurAktive, setNurAktive] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [zeilen, setZeilen] = useState<ImpersonationListItem[]>([]);

  function nurAktiveAendern(wert: boolean) {
    setNurAktive(wert);
    setCursor(undefined);
  }

  const params = new URLSearchParams();
  if (nurAktive) params.set("nurAktive", "true");
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  const path = `/api/admin/impersonations${qs ? `?${qs}` : ""}`;

  const { data, zusatz, loading, error, reload } = useApi<ImpersonationListItem[], { nextCursor?: string }>(path);

  useEffect(() => {
    if (data == null) return;
    setZeilen((vorherige) => (cursor ? [...vorherige, ...data] : data));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- s. Kopfkommentar AuditLogSeite.tsx
  }, [data]);

  const nextCursor = zusatz?.nextCursor;
  const zeigtLeer = !loading && !error && zeilen.length === 0;

  return (
    <>
      <PageHeader title="Impersonationen" subtitle="Support-Zugriffe plattformweit — aktive Grants und Historie." />

      <div className="card mb-4 flex items-center gap-3 p-4">
        <label className="flex items-center gap-2 font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
          <Checkbox
            checked={nurAktive}
            onChange={(e) => nurAktiveAendern(e.target.checked)}
          />
          Nur aktive anzeigen
        </label>
      </div>

      {error && <ErrorNote message={error} onRetry={reload} />}

      {loading && zeilen.length === 0 ? (
        <div className="card">
          <LoadingRows rows={6} />
        </div>
      ) : zeigtLeer ? (
        <div className="card">
          <EmptyState
            icon="support_agent"
            title="Keine Support-Zugriffe gefunden"
            hint={nurAktive ? "Aktuell laeuft kein Support-Zugriff." : "Es gab bisher keinen Support-Zugriff (Impersonation)."}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="handy-karten w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr>
                  <th className="table-header">Mandant</th>
                  <th className="table-header">Admin</th>
                  <th className="table-header">Grund</th>
                  <th className="table-header">Erstellt</th>
                  <th className="table-header">Läuft ab / beendet</th>
                  <th className="table-header">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/30 dark:divide-outline/30">
                {zeilen.map((zeile) => (
                  <tr key={zeile.id}>
                    <td className="table-cell font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
                      {zeile.tenantSlug === null ? (
                        <span className="font-normal italic text-on-surface-variant dark:text-secondary-fixed-dim">Mandant gelöscht</span>
                      ) : (
                        <>
                          <span>{zeile.tenantName}</span>{" "}
                          <span className="font-mono text-[11px] text-on-surface-variant dark:text-secondary-fixed-dim">
                            ({zeile.tenantSlug})
                          </span>
                        </>
                      )}
                    </td>
                    <td className="table-cell font-mono text-body-sm text-on-background dark:text-inverse-on-surface">{zeile.adminId}</td>
                    <td className="table-cell max-w-[280px] truncate font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {zeile.reason}
                    </td>
                    <td className="table-cell whitespace-nowrap font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {formatDateTime(zeile.createdAt)}
                    </td>
                    <td className="table-cell whitespace-nowrap font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {zeile.aktiv ? formatDateTime(zeile.expiresAt) : formatDateTime(zeile.endedAt)}
                    </td>
                    <td className="table-cell">
                      <StatusPill meta={zeile.aktiv ? AKTIV_META : BEENDET_META} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {nextCursor && (
            <div className="flex justify-center border-t border-outline-variant/30 p-3 dark:border-outline/30">
              <button type="button" className="btn-secondary" disabled={loading} onClick={() => setCursor(nextCursor)}>
                {loading ? "Lädt …" : "Mehr laden"}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
